import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const Schedules = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    prompt: '',
    schedule_type: 'daily',
    run_at: '',
    interval_minutes: 60,
    time_of_day: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    deliver_telegram: false,
  });

  const loadTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/schedule/tasks');
      setTasks(res.data || []);
    } catch (e) {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []);

  const createTask = async () => {
    try {
      const payload = { ...form };
      if (payload.schedule_type !== 'one_time') delete payload.run_at;
      if (payload.schedule_type !== 'interval') delete payload.interval_minutes;
      if (payload.schedule_type !== 'daily') delete payload.time_of_day;
      await axios.post('/api/schedule/tasks', payload);
      toast.success('Schedule created');
      setForm({ ...form, title: '', prompt: '' });
      loadTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const toggleTask = async (task) => {
    try {
      await axios.put(`/api/schedule/tasks/${task.id}`, { enabled: !task.enabled });
      loadTasks();
    } catch {
      toast.error('Failed to update schedule');
    }
  };

  const runNow = async (id) => {
    try {
      await axios.post(`/api/schedule/tasks/${id}/run`);
      toast.success('Task executed');
    } catch {
      toast.error('Failed to run task');
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`/api/schedule/tasks/${id}`);
      loadTasks();
    } catch {
      toast.error('Failed to delete schedule');
    }
  };

  const deleteAllTasks = async () => {
    if (!window.confirm('Delete ALL schedules and run history? This cannot be undone.')) return;
    try {
      const res = await axios.delete('/api/schedule/tasks');
      toast.success(res?.data?.message || 'All schedules deleted');
      loadTasks();
    } catch {
      toast.error('Failed to delete all schedules');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schedules</h1>
          <p className="text-gray-600 mt-2">Automate recurring prompts and optional Telegram delivery.</p>
        </div>
        <button className="text-sm px-3 py-2 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100" onClick={deleteAllTasks}>
          Delete All Schedules
        </button>
      </div>

      <div className="card mt-6 space-y-3">
        <input className="input-field" placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} />
        <textarea className="input-field" placeholder="Prompt to run" value={form.prompt} onChange={(e)=>setForm({...form,prompt:e.target.value})} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select className="input-field" value={form.schedule_type} onChange={(e)=>setForm({...form,schedule_type:e.target.value})}>
            <option value="daily">Daily</option>
            <option value="interval">Interval</option>
            <option value="one_time">One-time</option>
          </select>
          {form.schedule_type === 'daily' && (
            <input className="input-field" type="time" value={form.time_of_day} onChange={(e)=>setForm({...form,time_of_day:e.target.value})} />
          )}
          {form.schedule_type === 'interval' && (
            <input className="input-field" type="number" min="1" value={form.interval_minutes} onChange={(e)=>setForm({...form,interval_minutes:Number(e.target.value)})} placeholder="Minutes" />
          )}
          {form.schedule_type === 'one_time' && (
            <input className="input-field" type="datetime-local" value={form.run_at} onChange={(e)=>setForm({...form,run_at:e.target.value})} />
          )}
          <input className="input-field" placeholder="Timezone (e.g. Asia/Singapore)" value={form.timezone} onChange={(e)=>setForm({...form,timezone:e.target.value})} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.deliver_telegram} onChange={(e)=>setForm({...form,deliver_telegram:e.target.checked})} />
            Send to Telegram
          </label>
          <button className="btn-primary" onClick={createTask}>Create Schedule</button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? <p className="text-sm text-gray-500">Loading...</p> : null}
        {tasks.map((t) => (
          <div key={t.id} className="card flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="font-semibold text-gray-900">{t.title}</p>
              <p className="text-sm text-gray-600">{t.prompt}</p>
              <p className="text-xs text-gray-500 mt-1">{t.schedule_type} • tz: {t.timezone || 'UTC'} • next: {t.next_run_at ? new Date(t.next_run_at).toLocaleString() : '—'} • {t.enabled ? 'enabled' : 'disabled'}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-outline" onClick={()=>toggleTask(t)}>{t.enabled ? 'Pause' : 'Enable'}</button>
              <button className="btn-outline" onClick={()=>runNow(t.id)}>Run now</button>
              <button className="text-red-600 text-sm" onClick={()=>deleteTask(t.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Schedules;
